import { useLayoutEffect } from 'react';

const AXIS_LOCK_THRESHOLD_PX = 40;
const AXIS_DOMINANCE_RATIO = 2.2;
const AXIS_UNLOCK_OPPOSITE_PX = 28;

/**
 * Soft axis-lock per lo scroll 2D del calendario (solo mobile).
 * Lock solo dopo ~40px e se un asse domina chiaramente (ratio 2.2);
 * diagonale o gesto che diventa obliquo → axis='free', niente force scroll.
 * Passive listeners: non interferisce con click/drag su slot/task.
 */
const useMobileAxisLock = (containerRef, enabled) => {
  useLayoutEffect(() => {
    if (!enabled) return undefined;

    const el = containerRef.current;
    if (!el) return undefined;

    const gesture = {
      startX: 0,
      startY: 0,
      // null = undecided, 'x' | 'y' = locked, 'free' = diagonale / sblocco senza lock
      axis: null,
      lockedScrollLeft: 0,
      lockedScrollTop: 0,
      raf: 0,
    };

    const applyLock = () => {
      if (gesture.axis === 'y') {
        if (el.scrollLeft !== gesture.lockedScrollLeft) {
          el.scrollLeft = gesture.lockedScrollLeft;
        }
      } else if (gesture.axis === 'x') {
        if (el.scrollTop !== gesture.lockedScrollTop) {
          el.scrollTop = gesture.lockedScrollTop;
        }
      }
    };

    const scheduleLock = () => {
      if (gesture.axis !== 'x' && gesture.axis !== 'y') return;
      if (gesture.raf) return;
      gesture.raf = requestAnimationFrame(() => {
        gesture.raf = 0;
        applyLock();
      });
    };

    const reset = () => {
      gesture.axis = null;
      if (gesture.raf) {
        cancelAnimationFrame(gesture.raf);
        gesture.raf = 0;
      }
    };

    const onTouchStart = (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      gesture.startX = touch.clientX;
      gesture.startY = touch.clientY;
      gesture.axis = null;
      gesture.lockedScrollLeft = el.scrollLeft;
      gesture.lockedScrollTop = el.scrollTop;
      if (gesture.raf) {
        cancelAnimationFrame(gesture.raf);
        gesture.raf = 0;
      }
    };

    const onTouchMove = (event) => {
      if (event.touches.length !== 1) return;
      if (gesture.axis === 'free') return;

      const touch = event.touches[0];
      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      if (!gesture.axis) {
        if (absX < AXIS_LOCK_THRESHOLD_PX && absY < AXIS_LOCK_THRESHOLD_PX) {
          return;
        }

        if (absX > absY * AXIS_DOMINANCE_RATIO) {
          gesture.axis = 'x';
        } else if (absY > absX * AXIS_DOMINANCE_RATIO) {
          gesture.axis = 'y';
        } else {
          // Diagonale vera: niente lock per questo gesto
          gesture.axis = 'free';
          return;
        }

        gesture.lockedScrollLeft = el.scrollLeft;
        gesture.lockedScrollTop = el.scrollTop;
      } else if (gesture.axis === 'y' && absX > AXIS_UNLOCK_OPPOSITE_PX) {
        // Swipe verticale che diventa obliquo: smetti di forzare scrollLeft
        gesture.axis = 'free';
        if (gesture.raf) {
          cancelAnimationFrame(gesture.raf);
          gesture.raf = 0;
        }
        return;
      } else if (gesture.axis === 'x' && absY > AXIS_UNLOCK_OPPOSITE_PX) {
        // Swipe orizzontale che diventa obliquo: smetti di forzare scrollTop
        gesture.axis = 'free';
        if (gesture.raf) {
          cancelAnimationFrame(gesture.raf);
          gesture.raf = 0;
        }
        return;
      }

      scheduleLock();
    };

    const opts = { passive: true };
    el.addEventListener('touchstart', onTouchStart, opts);
    el.addEventListener('touchmove', onTouchMove, opts);
    el.addEventListener('touchend', reset, opts);
    el.addEventListener('touchcancel', reset, opts);

    return () => {
      reset();
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', reset);
      el.removeEventListener('touchcancel', reset);
    };
  }, [containerRef, enabled]);
};

export default useMobileAxisLock;
